import React from 'react';
import { Link } from 'gatsby';
import { string, number } from 'prop-types';
import styled from 'styled-components';
import { listingPath } from 'TMUtils/blogPaths';
import { spacing } from 'TMDesignSystem/spacing';
import { breakpoints } from 'TMConstants';

const Nav = styled.nav`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: ${spacing.small};
    margin: ${spacing.xxLarge} 0;
`;

const PageList = styled.div`
    display: flex;
    align-items: center;
    gap: ${spacing.tiny};

    @media (min-width: ${breakpoints.mobileTablet}px) {
        gap: ${spacing.small};
    }
`;

const cell = `
    align-items: center;
    border-radius: 30px;
    display: inline-flex;
    font-size: 0.875rem;
    font-weight: 700;
    justify-content: center;
    line-height: 1.5rem;
    min-width: 40px;
    padding: 8px 10px;
    text-decoration: none;
`;

const PageLink = styled(Link)`
    ${cell}
    background: ${({ theme }) => theme.colorSystem.primary[3]};
    border: 3px solid ${({ theme }) => theme.colorSystem.UIElements[4]};
    color: ${({ theme }) => theme.colorSystem.primary[1]};
    transition: all 0.25s;

    &:hover {
        background: ${({ theme }) => theme.colorSystem.UIElements[4]};
    }
`;

const CurrentPage = styled.span`
    ${cell}
    background: ${({ theme }) => theme.colorSystem.primary[4]};
    border: none;
    color: ${({ theme }) => theme.colorSystem.primary[3]};
`;

const Gap = styled.span`
    ${cell}
    color: ${({ theme }) => theme.colorSystem.UIText[4]};
    min-width: auto;
`;

const Step = styled(Link)`
    ${cell}
    background: none;
    color: ${({ theme }) => theme.colorSystem.primary[1]};
    gap: ${spacing.tiny};
    padding: 8px ${spacing.medium};

    &:hover {
        text-decoration: underline;
    }
`;

/**
 * The pages to render: always the first page, the last page and the pages
 * either side of the current one, with a gap marker wherever the sequence
 * jumps. `page: null` marks a gap.
 */
export const buildPageList = (currentPage, numPages) => {
    const wanted = [
        1,
        currentPage - 1,
        currentPage,
        currentPage + 1,
        numPages
    ];
    const visible = wanted
        .filter(
            (page, index) =>
                page >= 1 &&
                page <= numPages &&
                wanted.indexOf(page) === index
        )
        .sort((a, b) => a - b);

    return visible.reduce((items, page, index) => {
        if (index > 0 && page - visible[index - 1] > 1) {
            items.push({ key: `gap-${page}`, page: null });
        }
        items.push({ key: `page-${page}`, page });
        return items;
    }, []);
};

const Pagination = ({ basePath, currentPage, numPages, label }) => {
    if (numPages <= 1) {
        return null;
    }

    const renderPage = ({ key, page }) => {
        if (page === null) {
            return (
                <Gap key={key} aria-hidden="true">
                    &hellip;
                </Gap>
            );
        }

        if (page === currentPage) {
            return (
                <CurrentPage key={key} aria-current="page">
                    {page}
                </CurrentPage>
            );
        }

        return (
            <PageLink key={key} to={listingPath(basePath, page)}>
                {page}
            </PageLink>
        );
    };

    return (
        <Nav aria-label={label}>
            {currentPage > 1 && (
                <Step
                    to={listingPath(basePath, currentPage - 1)}
                    rel="prev">
                    &lsaquo; Previous
                </Step>
            )}
            <PageList>
                {buildPageList(currentPage, numPages).map(renderPage)}
            </PageList>
            {currentPage < numPages && (
                <Step
                    to={listingPath(basePath, currentPage + 1)}
                    rel="next">
                    Next &rsaquo;
                </Step>
            )}
        </Nav>
    );
};

Pagination.propTypes = {
    basePath: string.isRequired,
    currentPage: number.isRequired,
    numPages: number.isRequired,
    label: string
};

Pagination.defaultProps = {
    label: 'Pagination'
};

export default Pagination;
